import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  LinearProgress,
  IconButton,
  Divider,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import {
  SmartToy as AgentIcon,
  Category as CategorizeIcon,
  Receipt as InvoiceIcon,
  TrendingUp as AnalysisIcon,
  ShowChart as ForecastIcon,
  AccountBalance as ReconcileIcon,
  PlayArrow as ExecuteIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Schedule as PendingIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon,
  Close as CloseIcon,
  Lightbulb as RecommendationIcon,
  LocalFireDepartment as FireIcon,
  TrendingUp as TrendingUpIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  CompareArrows as CompareIcon,
  Search as SearchIcon,
  EmojiEvents as TrophyIcon,
  FilterCenterFocus as FocusIcon,
  Check as CheckIcon,
  Timeline as TimelineIcon,
} from '@mui/icons-material';
import axios from 'axios';
import Confetti from 'react-confetti';
import { ExpenseSwiper } from '../components/gamification/ExpenseSwiper';
import { TamagotchiWidget } from '../components/gamification/TamagotchiWidget';
import { GhostProfitWidget } from '../components/gamification/GhostProfitWidget';
import { ReviewIntegrations } from '../components/settings/ReviewIntegrations';
import { Switch, FormControlLabel } from '@mui/material';
import ForecastChart from '../components/ai/ForecastChart';
import ForecastSummary from '../components/ai/ForecastSummary';

interface Agent {
  id: string;
  name: string;
  description: string;
  icon: React.ReactElement;
  color: string;
  endpoint: string;
  status: 'idle' | 'running' | 'success' | 'error';
  lastRun?: Date;
  inputFields?: Array<{
    name: string;
    label: string;
    type: string;
    required: boolean;
    defaultValue?: any;
  }>;
}

interface AgentExecution {
  agent_id: string;
  status: string;
  result?: any;
  error?: string;
  timestamp: Date;
}

const AgentDashboard: React.FC = () => {
  const navigate = useNavigate();

  // Task ID to route mapping for Setup Quest
  const taskRouteMap: Record<string, string> = {
    'connect_bank': '/banking',
    'add_customer': '/customers',
    'first_invoice': '/invoices',
    'connect_hmrc': '/hmrc',
    'bank_account': '/banking',
    'customer': '/customers',
    'invoice': '/invoices',
    'hmrc': '/hmrc',
  };

  const handleTaskClick = (taskId: string, completed: boolean) => {
    if (completed) return; // Don't navigate if already completed
    const route = taskRouteMap[taskId] || Object.entries(taskRouteMap).find(([key]) => taskId.toLowerCase().includes(key))?.[1];
    if (route) {
      navigate(route);
    }
  };

  const [agents, setAgents] = useState<Agent[]>([
    {
      id: 'categorize_transaction',
      name: 'Transaction Categorization',
      description: 'Automatically categorize transactions with AI-powered analysis',
      icon: <CategorizeIcon />,
      color: '#1976d2',
      endpoint: '/api/v1/ai/agents/categorize-transaction',
      status: 'idle',
      inputFields: [
        { name: 'transaction_id', label: 'Transaction ID', type: 'number', required: true },
        { name: 'description', label: 'Description', type: 'text', required: false },
        { name: 'amount', label: 'Amount (£)', type: 'number', required: false }
      ]
    },
    {
      id: 'process_invoice',
      name: 'Invoice Processing',
      description: 'Extract and process invoice data automatically',
      icon: <InvoiceIcon />,
      color: '#2e7d32',
      endpoint: '/api/v1/ai/agents/process-invoice',
      status: 'idle',
      inputFields: [
        { name: 'invoice_id', label: 'Invoice ID', type: 'number', required: true }
      ]
    },
    {
      id: 'analyze_expenses',
      name: 'Expense Analysis',
      description: 'Analyze spending patterns and detect anomalies',
      icon: <AnalysisIcon />,
      color: '#ed6c02',
      endpoint: '/api/v1/ai/agents/analyze-expenses',
      status: 'idle',
      inputFields: []
    },
    {
      id: 'forecast',
      name: 'Financial Forecasting',
      description: 'Generate cash flow and revenue forecasts',
      icon: <ForecastIcon />,
      color: '#9c27b0',
      endpoint: '/api/v1/ai/agents/forecast',
      status: 'idle',
      inputFields: [
        { name: 'forecast_months', label: 'Forecast Months', type: 'number', required: false, defaultValue: 3 }
      ]
    },
    {
      id: 'reconcile',
      name: 'Account Reconciliation',
      description: 'Reconcile bank accounts and detect discrepancies',
      icon: <ReconcileIcon />,
      color: '#d32f2f',
      endpoint: '/api/v1/ai/agents/reconcile',
      status: 'idle',
      inputFields: [
        { name: 'account_id', label: 'Account ID', type: 'number', required: true }
      ]
    }
  ]);

  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [executionResult, setExecutionResult] = useState<any>(null);
  const [recentExecutions, setRecentExecutions] = useState<AgentExecution[]>([]);
  const [agentStatus, setAgentStatus] = useState<any>(null);
  const [inputValues, setInputValues] = useState<Record<string, any>>({});

  // Gamification State
  const [healthScore, setHealthScore] = useState<any>(null);
  const [milestones, setMilestones] = useState<any[]>([]);
  const [streakData, setStreakData] = useState<any>(null);
  const [benchmarks, setBenchmarks] = useState<any>(null);
  const [dailyStandup, setDailyStandup] = useState<any>(null);
  const [questData, setQuestData] = useState<any>(null);
  const [bounties, setBounties] = useState<any[]>([]);
  const [isFounderFocus, setIsFounderFocus] = useState(false);
  const [isQuestVisible, setIsQuestVisible] = useState(true);
  const [focusDialogOpen, setFocusDialogOpen] = useState(false);

  const [showConfetti, setShowConfetti] = useState(false);
  // Safe window access - avoid SSR/hydration issues
  const [windowSize, setWindowSize] = useState({ width: 1200, height: 800 });
  const [isMounted, setIsMounted] = useState(false);
  const [isDataLoading, setIsDataLoading] = useState(true);

  // Set mounted state and window size after component mounts (client-side only)
  useEffect(() => {
    setIsMounted(true);
    if (typeof window !== 'undefined') {
      setWindowSize({ width: window.innerWidth, height: window.innerHeight });
    }
    
    // Load initial data
    const loadInitialData = async () => {
      setIsDataLoading(true);
      try {
        // Run these in parallel but don't block on failures
        await Promise.allSettled([
          checkAgentStatus(),
          fetchGamificationData(),
        ]);
        loadRecentExecutions();
      } catch (err) {
        console.error('Failed to load initial data:', err);
      } finally {
        setIsDataLoading(false);
      }
    };
    
    loadInitialData();

    const handleResize = () => setWindowSize({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchGamificationData = async () => {
    try {
      const scoreRes = await axios.get('/api/cfo/health-score/test-customer-123');
      setHealthScore(scoreRes.data);

      const milestonesRes = await axios.get('/api/cfo/milestones/test-customer-123');
      setMilestones(milestonesRes.data);

      const streakRes = await axios.get('/api/cfo/streak/test-customer-123');
      setStreakData(streakRes.data);

      const standupRes = await axios.get('/api/cfo/daily-standup/test-customer-123');
      setDailyStandup(standupRes.data);

      const questRes = await axios.get('/api/cfo/onboarding-quest/test-customer-123');
      setQuestData(questRes.data);

      const bountiesRes = await axios.get('/api/cfo/bounties/test-customer-123');
      setBounties(bountiesRes.data);

      if (milestonesRes.data && milestonesRes.data.length > 0) {
        // Simple logic for checking new unlocks could go here
        // setShowConfetti(true);
        // setTimeout(() => setShowConfetti(false), 8000);
      }
    } catch (e) {
      console.error("Gamification fetch failed", e);
    }
  };

  const checkAgentStatus = async () => {
    try {
      const response = await axios.get('/api/v1/ai/agents/status');
      setAgentStatus(response.data);
    } catch (err) {
      console.error('Failed to check agent status:', err);
    }
  };

  const loadRecentExecutions = () => {
    // Load from localStorage for demo
    const stored = localStorage.getItem('agent_executions');
    if (stored) {
      const executions = JSON.parse(stored);
      setRecentExecutions(executions.slice(0, 10));
    }
  };

  const saveExecution = (execution: AgentExecution) => {
    const stored = localStorage.getItem('agent_executions');
    const executions = stored ? JSON.parse(stored) : [];
    executions.unshift(execution);
    localStorage.setItem('agent_executions', JSON.stringify(executions.slice(0, 50)));
    loadRecentExecutions();
  };

  const handleExecuteAgent = (agent: Agent) => {
    setSelectedAgent(agent);
    setExecutionResult(null);
    setInputValues({});
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedAgent(null);
    setExecutionResult(null);
    setInputValues({});
  };

  const executeAgent = async () => {
    if (!selectedAgent) return;

    setExecuting(true);
    setExecutionResult(null);

    // Update agent status
    setAgents(prev => prev.map(a =>
      a.id === selectedAgent.id ? { ...a, status: 'running' } : a
    ));

    try {
      const params = new URLSearchParams();
      params.append('company_id', '1');

      Object.entries(inputValues).forEach(([key, value]) => {
        if (value !== undefined && value !== '') {
          params.append(key, value.toString());
        }
      });

      const response = await axios.post(
        `${selectedAgent.endpoint}?${params.toString()}`,
        {},
        { headers: { 'Content-Type': 'application/json' } }
      );

      setExecutionResult(response.data);

      // Update agent status
      setAgents(prev => prev.map(a =>
        a.id === selectedAgent.id ? { ...a, status: 'success', lastRun: new Date() } : a
      ));

      // Save execution
      saveExecution({
        agent_id: selectedAgent.id,
        status: 'success',
        result: response.data,
        timestamp: new Date()
      });

    } catch (err: any) {
      const errorMsg = err.response?.data?.detail || err.message || 'Execution failed';
      setExecutionResult({ error: errorMsg });

      // Update agent status
      setAgents(prev => prev.map(a =>
        a.id === selectedAgent.id ? { ...a, status: 'error' } : a
      ));

      // Save execution
      saveExecution({
        agent_id: selectedAgent.id,
        status: 'error',
        error: errorMsg,
        timestamp: new Date()
      });
    } finally {
      setExecuting(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'running': return <CircularProgress size={20} />;
      case 'success': return <SuccessIcon color="success" />;
      case 'error': return <ErrorIcon color="error" />;
      default: return <PendingIcon color="action" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'running': return 'info';
      case 'success': return 'success';
      case 'error': return 'error';
      default: return 'default';
    }
  };

  const [swiperOpen, setSwiperOpen] = useState(false);

  const handleClaimBounty = (bountyId: string) => {
    if (bountyId.includes('expense')) {
      setSwiperOpen(true);
    } else {
      alert("Bounty claimed! +150 XP");
    }
  };

  const handleSwiperComplete = () => {
    setSwiperOpen(false);
    setShowConfetti(true);
    setTimeout(() => setShowConfetti(false), 5000);
    setBounties(prev => prev.filter(b => !b.id.includes('expense')));
  };

  // Only wait for client-side mount - don't block on data loading
  if (!isMounted) {
    return (
      <Box sx={{ 
        width: '100%', 
        height: 'calc(100vh - 200px)', 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center', 
        justifyContent: 'center',
        gap: 3
      }}>
        <AgentIcon sx={{ fontSize: 60, color: 'primary.main' }} />
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            Loading AI Agent Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Initializing your financial agents...
          </Typography>
        </Box>
        <CircularProgress size={40} />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', py: 2 }}>
      {showConfetti && <Confetti numberOfPieces={500} recycle={false} />}

      <ExpenseSwiper
        open={swiperOpen}
        onClose={() => setSwiperOpen(false)}
        onComplete={handleSwiperComplete}
      />

      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <AgentIcon sx={{ fontSize: 40, color: 'primary.main' }} />
            <Box>
              <Typography variant="h4" fontWeight="bold">
                AI Agent Dashboard
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Execute specialized financial AI agents for automated tasks
              </Typography>
            </Box>
          </Box>
          <Button
            startIcon={<RefreshIcon />}
            onClick={checkAgentStatus}
            variant="outlined"
          >
            Refresh Status
          </Button>
          <FormControlLabel
            control={<Switch checked={isFounderFocus} onChange={(e) => setIsFounderFocus(e.target.checked)} />}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <FocusIcon color={isFounderFocus ? "primary" : "disabled"} />
                <Typography variant="body2" fontWeight={isFounderFocus ? "bold" : "regular"}>
                  Founder Focus
                </Typography>
              </Box>
            }
            sx={{ ml: 2 }}
          />
        </Box>
      </Box>

      {/* Founder Focus Mode: Hide Everything except Health Score */}
      {isFounderFocus && healthScore ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh', flexDirection: 'column' }}>
          <Typography variant="h4" gutterBottom>NORTH STAR METRIC</Typography>
          <Paper sx={{ p: 5, textAlign: 'center', borderRadius: 8, background: 'linear-gradient(135deg, #1a237e 0%, #0d47a1 100%)', color: 'white', maxWidth: 600 }}>
            <Typography variant="h5" gutterBottom>Business Health Score</Typography>
            <Box sx={{ position: 'relative', display: 'inline-flex', my: 4 }}>
              <CircularProgress variant="determinate" value={100} size={200} sx={{ color: 'rgba(255,255,255,0.1)' }} />
              <CircularProgress variant="determinate" value={healthScore.score} size={200} sx={{ color: '#00e676', position: 'absolute', left: 0 }} />
              <Box sx={{ top: 0, left: 0, bottom: 0, right: 0, position: 'absolute', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="h1" component="div" fontWeight="bold">{healthScore.score}</Typography>
              </Box>
            </Box>
            <Typography variant="h6" sx={{ opacity: 0.9 }}>{healthScore.insight}</Typography>
            <Button
              variant="contained"
              color="success"
              sx={{ mt: 4 }}
              size="large"
              onClick={() => setFocusDialogOpen(true)}
            >
              Improve This Score
            </Button>
          </Paper>

          {/* Focus Mode Action Dialog */}
          <Dialog open={focusDialogOpen} onClose={() => setFocusDialogOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ textAlign: 'center', fontWeight: 'bold' }}>
              🚀 Strategic Priorities
            </DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
                Focus on these 3 high-impact actions to improve your Health Score (+15 pts).
              </Typography>
              <Grid container spacing={2}>
                {bounties && bounties.slice(0, 3).map((bounty: any) => (
                  <Grid item xs={12} key={bounty.id}>
                    <Paper sx={{ p: 2, border: '1px solid #e0e0e0', display: 'flex', alignItems: 'center', gap: 2, '&:hover': { bgcolor: '#f5f5f5' } }}>
                      <Box sx={{ fontSize: '1.5rem' }}>{bounty.icon}</Box>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="subtitle2" fontWeight="bold">{bounty.title}</Typography>
                        <Typography variant="caption" color="text.secondary">{bounty.desc}</Typography>
                      </Box>
                      <Button size="small" variant="contained" color={bounty.type === 'dead_stock' ? 'error' : 'primary'} onClick={() => handleClaimBounty(bounty.id)}>
                        {bounty.reward}
                      </Button>
                    </Paper>
                  </Grid>
                ))}
                {(!bounties || bounties.length === 0) && (
                  <Grid item xs={12}>
                    <Alert severity="success">You have no pending priorities! Great job.</Alert>
                  </Grid>
                )}
              </Grid>
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
              <Button onClick={() => setFocusDialogOpen(false)} color="inherit">Close Focus Mode</Button>
            </DialogActions>
          </Dialog>
        </Box>
      ) : (
        <>
          {/* Daily Standup & Streak Header (Prioritized) */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={8}>
              {dailyStandup && (
                <Paper sx={{ p: 2, height: '100%', display: 'flex', alignItems: 'center', gap: 2, bgcolor: 'primary.50', borderLeft: '4px solid #1976d2' }}>
                  <IconButton color="primary" sx={{ bgcolor: 'white' }}>
                    <PlayIcon />
                  </IconButton>
                  <Box>
                    <Typography variant="subtitle2" fontWeight="bold">{dailyStandup.greeting}</Typography>
                    <Typography variant="body2">{dailyStandup.summary_text}</Typography>
                  </Box>
                </Paper>
              )}
            </Grid>
            <Grid item xs={12} md={4}>
              {streakData && (
                <Paper sx={{ p: 2, height: '100%', bgcolor: streakData.at_risk ? '#fff0f0' : '#f0fff4', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="caption" fontWeight="bold" color="text.secondary">CURRENT STREAK</Typography>
                    <Typography variant="h4" fontWeight="bold" color={streakData.at_risk ? 'error' : 'success'}>
                      {streakData.current_streak} Days 🔥
                    </Typography>
                  </Box>
                  <TimelineIcon sx={{ fontSize: 40, opacity: 0.5 }} />
                </Paper>
              )}

            </Grid>
          </Grid>



          {/* The Setup Quest Widget */}
          {questData && isQuestVisible && (
            <Paper sx={{ p: 3, mb: 3, bgcolor: '#f8fbfc', border: '1px solid #e0e7ff', position: 'relative' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Typography variant="h6" fontWeight="bold">🚀 The Setup Quest</Typography>
                <Box>
                  <Chip label={`${questData.progress}% Complete`} color="primary" size="small" sx={{ mr: 1 }} />
                  <IconButton size="small" onClick={() => setIsQuestVisible(false)} aria-label="Dismiss Quest">
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>

              <Box sx={{ width: '100%', mr: 1, mb: 2 }}>
                <LinearProgress variant="determinate" value={questData.progress} sx={{ height: 10, borderRadius: 5 }} />
              </Box>

              <Grid container spacing={2}>
                {questData.tasks.map((task: any) => (
                  <Grid item xs={12} sm={3} key={task.id}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        opacity: task.completed ? 0.6 : 1,
                        cursor: task.completed ? 'default' : 'pointer',
                        '&:hover': task.completed ? {} : {
                          bgcolor: 'action.hover',
                          borderRadius: 1,
                          px: 1,
                          mx: -1
                        }
                      }}
                      onClick={() => handleTaskClick(task.id, task.completed)}
                    >
                      {task.completed ? <CheckIcon color="success" /> : <Box sx={{ width: 24, height: 24, borderRadius: '50%', border: '2px solid #ccc' }} />}
                      <Typography
                        variant="body2"
                        sx={{
                          textDecoration: task.completed ? 'line-through' : 'none',
                          color: task.completed ? 'text.secondary' : 'primary.main',
                          fontWeight: task.completed ? 'normal' : 500
                        }}
                      >
                        {task.label}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>

              {/* Quote of the Day */}
              <Divider sx={{ my: 2 }} />
              <Typography variant="caption" sx={{ fontStyle: 'italic', color: 'text.secondary', display: 'flex', gap: 1 }}>
                💡 <b>Daily Wisdom:</b> "{questData.quote}"
              </Typography>
            </Paper>
          )}



          {/* Ghost Mode: Profit Race */}
          <GhostProfitWidget />

          {/* Dashboard Actions: Pulse & Bounties */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              ⚡ Actions & Alerts <Chip label={`${(bounties?.length || 0)} Pending`} color="warning" size="small" />
            </Typography>
            <Grid container spacing={2}>
              {/* Tamagotchi Card (md=6) */}
              <Grid item xs={12} md={6}>
                <TamagotchiWidget />
              </Grid>

              {/* Bounty Cards (md=6) */}
              {bounties && bounties.map((bounty: any) => {
                const isDeadStock = bounty.type === 'dead_stock';
                return (
                  <Grid item xs={12} md={6} key={bounty.id}>
                    <Paper sx={{
                      p: 2,
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      borderLeft: isDeadStock ? 'none' : '4px solid #ed6c02',
                      bgcolor: isDeadStock ? '#fff8e1' : '#fff7ed', // Yellow textur-ish for Wanted
                      backgroundImage: isDeadStock ? 'url("https://www.transparenttextures.com/patterns/aged-paper.png")' : 'none',
                      border: isDeadStock ? '2px dashed #8d6e63' : 'none'
                    }}>
                      <Box sx={{ fontSize: '2rem' }}>{bounty.icon}</Box>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="subtitle1" fontWeight="bold" sx={{ fontFamily: isDeadStock ? 'Courier New, Courier, monospace' : 'inherit', letterSpacing: isDeadStock ? -0.5 : 0 }}>
                          {bounty.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">{bounty.desc}</Typography>
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <Chip label={bounty.reward} color={isDeadStock ? "error" : "warning"} variant="filled" sx={{ fontWeight: 'bold' }} />
                        <Button
                          size="small"
                          variant="text"
                          color={isDeadStock ? "error" : "warning"}
                          sx={{ display: 'block', mt: 1 }}
                          onClick={() => handleClaimBounty(bounty.id)}
                        >
                          {isDeadStock ? 'CAPTURE' : 'CLAIM'}
                        </Button>
                      </Box>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          </Box>

          {agentStatus && (
            <Alert severity="success" icon={<SuccessIcon />}>
              AI System Active • Version {agentStatus.version} • {agentStatus.agents_available} agents available
            </Alert>
          )}




          {/* Gamification Section: Business Health & Milestones */}
          {showConfetti && <Confetti width={windowSize.width} height={windowSize.height} recycle={false} numberOfPieces={500} />}

          {
            healthScore && (
              <Grid container spacing={3} sx={{ mb: 4 }}>
                {/* Health Score Card */}
                <Grid item xs={12} md={4}>
                  <Paper sx={{ p: 3, textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #1a237e 0%, #0d47a1 100%)', color: 'white' }}>
                    <Typography variant="h6" gutterBottom>Business Health Score</Typography>
                    <Box sx={{ position: 'relative', display: 'inline-flex', my: 2 }}>
                      <CircularProgress variant="determinate" value={100} size={120} sx={{ color: 'rgba(255,255,255,0.1)' }} />
                      <CircularProgress variant="determinate" value={healthScore.score} size={120} sx={{ color: '#00e676', position: 'absolute', left: 0 }} />
                      <Box sx={{ top: 0, left: 0, bottom: 0, right: 0, position: 'absolute', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography variant="h3" component="div" fontWeight="bold">{healthScore.score}</Typography>
                      </Box>
                    </Box>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>{healthScore.insight}</Typography>
                    <Chip label="Healthy" color="success" size="small" sx={{ mt: 1, bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }} />
                  </Paper>
                </Grid>

                {/* Metrics Breakdown */}
                <Grid item xs={12} md={4}>
                  <Paper sx={{ p: 3, height: '100%' }}>
                    <Typography variant="h6" gutterBottom>Performance Metrics</Typography>
                    <Box sx={{ mt: 2 }}>
                      <Box sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="body2">Revenue Health</Typography>
                          <Typography variant="body2" fontWeight="bold">{healthScore.metrics?.revenue_health}%</Typography>
                        </Box>
                        <LinearProgress variant="determinate" value={healthScore.metrics?.revenue_health} color="success" sx={{ height: 8, borderRadius: 4 }} />
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="body2">Expense Control</Typography>
                          <Typography variant="body2" fontWeight="bold">{healthScore.metrics?.expense_health}%</Typography>
                        </Box>
                        <LinearProgress variant="determinate" value={healthScore.metrics?.expense_health} color="warning" sx={{ height: 8, borderRadius: 4 }} />
                      </Box>
                      <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="body2">Customer Retention</Typography>
                          <Typography variant="body2" fontWeight="bold">{healthScore.metrics?.retention_health}%</Typography>
                        </Box>
                        <LinearProgress variant="determinate" value={healthScore.metrics?.retention_health} color="primary" sx={{ height: 8, borderRadius: 4 }} />
                      </Box>
                    </Box>
                  </Paper>
                </Grid>

                {/* Milestones / Badges */}
                <Grid item xs={12} md={4}>
                  <Paper sx={{ p: 3, height: '100%' }}>
                    <Typography variant="h6" gutterBottom>Founder Journey</Typography>
                    <List dense>
                      {milestones.map((m: any, i: number) => (
                        <ListItem key={i}>
                          <ListItemIcon sx={{ fontSize: 24 }}>{m.icon}</ListItemIcon>
                          <ListItemText
                            primary={m.name}
                            secondary={m.description}
                            primaryTypographyProps={{ fontWeight: m.unlocked ? 'bold' : 'normal', color: m.unlocked ? 'text.primary' : 'text.disabled' }}
                          />
                          {m.unlocked ? <SuccessIcon color="success" fontSize="small" /> : <Typography variant="caption" color="text.secondary">{m.progress}%</Typography>}
                        </ListItem>
                      ))}
                    </List>
                  </Paper>
                </Grid>

                {/* Peer Benchmarks */}
                {benchmarks && (
                  <Grid item xs={12}>
                    <Paper sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <CompareIcon color="primary" />
                        <Typography variant="h6">Industry Benchmarks ({benchmarks.industry})</Typography>
                      </Box>
                      <Grid container spacing={3}>
                        {benchmarks.metrics.map((metric: any, i: number) => (
                          <Grid item xs={12} md={6} key={i}>
                            <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 2 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="subtitle1" fontWeight="bold">{metric.name}</Typography>
                                <Chip
                                  label={metric.status.toUpperCase()}
                                  color={metric.status === 'good' ? 'success' : 'warning'}
                                  size="small"
                                />
                              </Box>
                              <Typography variant="body2" color="text.secondary" gutterBottom>{metric.message}</Typography>

                              <Box sx={{ mt: 2 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                  <Typography variant="caption">You: {metric.your_value}%</Typography>
                                  <Typography variant="caption">Top 10%: {metric.top_10_percent_value}%</Typography>
                                </Box>
                                <LinearProgress
                                  variant="determinate"
                                  value={(metric.your_value / (metric.top_10_percent_value * 1.5)) * 100}
                                  color={metric.status === 'good' ? 'success' : 'warning'}
                                  sx={{ height: 10, borderRadius: 5 }}
                                />
                              </Box>
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    </Paper>
                  </Grid>
                )}
              </Grid>
            )
          }

          {/* Agent Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {agents.map((agent) => (
              <Grid item xs={12} md={6} lg={4} key={agent.id}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderTop: `4px solid ${agent.color}`,
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 4
                    }
                  }}
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <Box sx={{
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: `${agent.color}15`,
                        color: agent.color
                      }}>
                        {agent.icon}
                      </Box>
                      <Chip
                        icon={getStatusIcon(agent.status)}
                        label={agent.status.toUpperCase()}
                        size="small"
                        color={getStatusColor(agent.status) as any}
                      />
                    </Box>

                    <Typography variant="h6" gutterBottom fontWeight="bold">
                      {agent.name}
                    </Typography>

                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {agent.description}
                    </Typography>

                    {agent.lastRun && (
                      <Typography variant="caption" color="text.secondary">
                        Last run: {agent.lastRun.toLocaleString()}
                      </Typography>
                    )}
                  </CardContent>

                  <CardActions sx={{ p: 2, pt: 0 }}>
                    <Button
                      fullWidth
                      variant="contained"
                      startIcon={<ExecuteIcon />}
                      onClick={() => handleExecuteAgent(agent)}
                      disabled={agent.status === 'running'}
                      sx={{ bgcolor: agent.color, '&:hover': { bgcolor: agent.color, opacity: 0.9 } }}
                    >
                      Execute Agent
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Recent Executions */}
          {
            recentExecutions.length > 0 && (
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom fontWeight="bold">
                  Recent Executions
                </Typography>
                <List>
                  {recentExecutions.map((execution, index) => {
                    const agent = agents.find(a => a.id === execution.agent_id);
                    return (
                      <React.Fragment key={index}>
                        <ListItem>
                          <ListItemIcon>
                            {agent?.icon || <AgentIcon />}
                          </ListItemIcon>
                          <ListItemText
                            primary={agent?.name || execution.agent_id}
                            secondary={`${execution.status.toUpperCase()} • ${new Date(execution.timestamp).toLocaleString()}`}
                          />
                          <Chip
                            label={execution.status}
                            size="small"
                            color={execution.status === 'success' ? 'success' : 'error'}
                          />
                        </ListItem>
                        {index < recentExecutions.length - 1 && <Divider />}
                      </React.Fragment>
                    );
                  })}
                </List>
              </Paper>
            )
          }
        </>
      )}

      {/* Execution Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth={selectedAgent?.id === 'forecast' && executionResult ? 'xl' : 'md'}
        fullWidth
        fullScreen={selectedAgent?.id === 'forecast' && executionResult ? true : false}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {selectedAgent && (
              <Box sx={{
                p: 1,
                borderRadius: 2,
                bgcolor: `${selectedAgent.color}15`,
                color: selectedAgent.color
              }}>
                {selectedAgent.icon}
              </Box>
            )}
            <Box>
              <Typography variant="h6">{selectedAgent?.name}</Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedAgent?.description}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent>
          {executing && (
            <Box sx={{ mb: 3 }}>
              <LinearProgress />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
                Executing agent...
              </Typography>
            </Box>
          )}

          {!executionResult && !executing && selectedAgent?.inputFields && selectedAgent.inputFields.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Input Parameters
              </Typography>
              <Grid container spacing={2}>
                {selectedAgent.inputFields.map((field) => (
                  <Grid item xs={12} key={field.name}>
                    <TextField
                      fullWidth
                      label={field.label}
                      type={field.type}
                      required={field.required}
                      value={inputValues[field.name] || field.defaultValue || ''}
                      onChange={(e) => setInputValues(prev => ({
                        ...prev,
                        [field.name]: field.type === 'number' ? parseFloat(e.target.value) : e.target.value
                      }))}
                      helperText={field.required ? 'Required' : 'Optional'}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          {!executionResult && !executing && selectedAgent?.inputFields?.length === 0 && (
            <Alert severity="info" icon={<InfoIcon />}>
              This agent doesn't require any input parameters. Click "Execute" to run.
            </Alert>
          )}

          {executionResult && (
            <Box sx={{ mt: 2 }}>
              {executionResult.error ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Execution Failed
                  </Typography>
                  <Typography variant="body2">
                    {executionResult.error}
                  </Typography>
                </Alert>
              ) : (
                <>
                  <Alert severity="success" sx={{ mb: 2 }}>
                    <Typography variant="subtitle2">
                      Agent executed successfully!
                    </Typography>
                  </Alert>

                  {/* Display Results */}

                  {/* Special handling for Forecast Agent */}
                  {selectedAgent?.id === 'forecast' && executionResult.results?.forecast && (
                    <Box sx={{ mb: 3 }}>
                      <ForecastChart data={executionResult.results.forecast} />
                      <Box sx={{ mt: 3 }}>
                        <ForecastSummary data={executionResult.results.forecast} />
                      </Box>
                    </Box>
                  )}

                  <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                      Results
                    </Typography>
                    <Divider sx={{ my: 1 }} />

                    {/* Status */}
                    {executionResult.status && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                          Status
                        </Typography>
                        <Typography variant="body2">
                          {executionResult.status}
                        </Typography>
                      </Box>
                    )}

                    {/* Recommendations */}
                    {executionResult.recommendations && executionResult.recommendations.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <RecommendationIcon fontSize="small" />
                          Recommendations
                        </Typography>
                        <List dense>
                          {executionResult.recommendations.map((rec: any, idx: number) => (
                            <ListItem key={idx}>
                              <ListItemText
                                primary={typeof rec === 'string' ? rec : rec.recommendation || JSON.stringify(rec)}
                                primaryTypographyProps={{ variant: 'body2' }}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    )}

                    {/* Raw Data */}
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Raw Response
                      </Typography>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 1,
                          mt: 0.5,
                          bgcolor: 'background.paper',
                          maxHeight: 300,
                          overflow: 'auto',
                          fontFamily: 'monospace',
                          fontSize: '0.75rem'
                        }}
                      >
                        <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                          {JSON.stringify(executionResult, null, 2)}
                        </pre>
                      </Paper>
                    </Box>
                  </Paper>
                </>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button onClick={handleCloseDialog}>
            Close
          </Button>
          {!executionResult && (
            <Button
              variant="contained"
              onClick={executeAgent}
              disabled={executing}
              startIcon={executing ? <CircularProgress size={16} /> : <ExecuteIcon />}
            >
              {executing ? 'Executing...' : 'Execute'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AgentDashboard;
